/**
 * Devices list + reset / factory-reset / remove actions per device,
 * plus the "+ Add device" entry-point.
 */

import { useState } from 'react';
import { updateDoc, serverTimestamp } from 'firebase/firestore';

import { useDevices } from '@/hooks/useDevices';
import { useDeviceStatus } from '@/hooks/useDeviceStatus';
import { useMyRole } from '@/hooks/useMyRole';
import { useNow } from '@/hooks/useNow';
import { formatLastSeen, isDeviceOnline } from '@/lib/deviceStatus';
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { AddDeviceDialog } from '@/components/AddDeviceDialog';
import { unlinkDevice } from '@/lib/callables';
import { firestoreWithRetry } from '@/lib/firestoreRetry';
import { deviceRef } from '@/lib/firestore';

interface Props {
  aid: string;
}

export function DevicesSection({ aid }: Props) {
  const { devices, loading } = useDevices(aid);
  const status = useDeviceStatus(aid);
  const now = useNow(30_000);
  const [addOpen, setAddOpen] = useState(false);
  // Adding, unlinking (unlink_device) and relabelling (device-mirror
  // update) are all admin-only server-side. Caregivers see the list + online
  // status read-only rather than controls that would fail.
  const isAdmin = useMyRole(aid) === 'admin';

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <p className="text-sm text-ink-soft">
          Buttons paired with this person. Each button's setup code is on the
          sticker on the button (and in the sticker's QR code).
        </p>
        {isAdmin && <Button onClick={() => setAddOpen(true)}>+ Add button</Button>}
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : devices.length === 0 ? (
        <Card>
          <CardBody>
            <p className="text-sm text-ink-soft">
              {isAdmin ? (
                <>
                  No buttons yet. Tap{' '}
                  <span className="font-medium text-ink">+ Add button</span> above to
                  pair one you already have, or to order one.
                </>
              ) : (
                'No buttons yet.'
              )}
            </p>
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-3">
          {devices.map((d) => {
            const st = status.byId[d.id];
            return (
              <DeviceCard
                key={d.id}
                aid={aid}
                deviceId={d.id}
                roomLabel={d.roomLabel ?? ''}
                lastSeen={st ? st.lastSeen : undefined}
                thresholdMinutes={status.thresholdMinutes}
                now={now}
                isAdmin={isAdmin}
              />
            );
          })}
        </div>
      )}

      {isAdmin && <AddDeviceDialog open={addOpen} onOpenChange={setAddOpen} aid={aid} />}

      {devices.length > 0 && <WifiRecoveryHelp />}
    </div>
  );
}

function WifiRecoveryHelp() {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <CardBody>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between text-left text-sm font-medium text-ink"
        >
          <span>If a button stops working after a Wi-Fi change</span>
          <span aria-hidden className="text-ink-soft">{open ? '▾' : '▸'}</span>
        </button>
        {open && (
          <div className="mt-3 space-y-3 text-sm text-ink-soft">
            <p>
              Most outages (router reboot, brief drop-out) self-recover within
              about 30 minutes — no action needed.
            </p>
            <p>
              <span className="font-medium text-ink">Changed your Wi-Fi password or router?</span>{' '}
              The button drops offline (it can&apos;t reach the old network) and,
              after a few minutes, reopens its own setup page on its own. Connect a
              phone or laptop to the button&apos;s{' '}
              <span className="font-medium text-ink">RemindMeVoice-Setup</span>{' '}
              network and enter the new Wi-Fi details when the page opens — if it
              doesn&apos;t open by itself, type{' '}
              <span className="font-mono">http://192.168.4.1</span> into the
              browser&apos;s address bar. Your
              relative&apos;s calendar and setup are kept — you don&apos;t need to
              add the button again, and there&apos;s no button-holding to do.
            </p>
            <p>
              <span className="font-medium text-ink">Moving it to a different network while the old one still works?</span>{' '}
              It won&apos;t drop offline on its own, so use{' '}
              <span className="font-medium text-ink">Unlink button</span>{' '}
              above first, then set it up again on the new network.
            </p>
            <p className="text-ink-soft/80">
              (Holding the button for 60 seconds is a full factory reset — it
              disconnects the button from your account as well as clearing its
              Wi-Fi. You only need that to pass the button on to someone else
              without using Remove.)
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function DeviceStatusLine({
  lastSeen,
  thresholdMinutes,
  now,
}: {
  /** undefined = status not loaded yet; null = never checked in. */
  lastSeen: string | null | undefined;
  thresholdMinutes: number;
  now: number;
}) {
  if (lastSeen === undefined) return null; // don't flicker before first load
  const lastSeenMs = lastSeen ? Date.parse(lastSeen) : null;
  const online = isDeviceOnline(lastSeenMs, now, thresholdMinutes);
  const detail =
    lastSeenMs === null
      ? 'never checked in'
      : `last checked in ${formatLastSeen(lastSeenMs, now)}`;
  return (
    <div className="mt-1.5 flex items-center gap-1.5 text-xs">
      <span
        aria-hidden
        className={`inline-block h-2 w-2 rounded-full ${online ? 'bg-green-500' : 'bg-amber-500'}`}
      />
      <span className={online ? 'text-ink-soft' : 'text-amber-700'}>
        {online ? 'Online' : 'Offline'} · {detail}
      </span>
    </div>
  );
}

function DeviceCard({
  aid,
  deviceId,
  roomLabel,
  lastSeen,
  thresholdMinutes,
  now,
  isAdmin,
}: {
  aid: string;
  deviceId: string;
  roomLabel: string;
  lastSeen: string | null | undefined;
  thresholdMinutes: number;
  now: number;
  isAdmin: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [room, setRoom] = useState(roomLabel);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle>{roomLabel || 'Unlabeled button'}</CardTitle>
            <CardDescription>{deviceId}</CardDescription>
            <DeviceStatusLine
              lastSeen={lastSeen}
              thresholdMinutes={thresholdMinutes}
              now={now}
            />
          </div>
          {isAdmin && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                // Seed the draft from the LIVE label every time the editor
                // opens — the once-at-mount useState default went stale if
                // the label changed (another tab, an earlier edit+cancel).
                if (!editing) setRoom(roomLabel);
                setEditing((v) => !v);
              }}
            >
              {editing ? 'Cancel' : 'Edit'}
            </Button>
          )}
        </div>
      </CardHeader>
      {isAdmin && (
      <CardBody className="space-y-4">
        {editing && (
          <form
            className="flex flex-col gap-3"
            onSubmit={async (event) => {
              event.preventDefault();
              setBusy(true);
              setMsg(null);
              try {
                await firestoreWithRetry(
                  () =>
                    updateDoc(deviceRef(aid, deviceId), {
                      roomLabel: room.trim(),
                      updatedAt: serverTimestamp(),
                    }),
                  'updateDeviceMirror',
                );
                setEditing(false);
              } catch (e) {
                setMsg(e instanceof Error ? e.message : 'Save failed.');
              } finally {
                setBusy(false);
              }
            }}
          >
            <Field label="Room label" htmlFor={`room-${deviceId}`}>
              <Input
                id={`room-${deviceId}`}
                value={room}
                onChange={(e) => setRoom(e.target.value)}
                maxLength={100}
              />
            </Field>
            <div>
              <Button type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </form>
        )}

        <div className="flex flex-wrap gap-2">
          {/* Single Unlink action. Disconnects the device from this account
              immediately (the card drops from the list) AND clears its Wi-Fi on
              its next check-in, so it drops back to setup mode for the next owner
              — while KEEPING its setup code (the printed sticker stays valid, so
              the next person claims it with the same code). It is NOT a factory
              wipe: to fully reset a device's identity, reflash it. Wi-Fi-password
              changes don't need this either — the device's own offline
              button-hold drops to the captive portal while keeping the claim. */}
          <Button
            variant="danger"
            size="sm"
            disabled={busy}
            onClick={async () => {
              if (
                !confirm(
                  'Unlink this button?\n\n' +
                    'It is disconnected from this account straight away and drops back to ' +
                    'setup mode (clears its Wi-Fi) within about 5 minutes, on its next ' +
                    'check-in. Its setup code and sticker stay valid, so the next person can ' +
                    'set it up with the same code.',
                )
              )
                return;
              setBusy(true);
              setMsg(null);
              try {
                await unlinkDevice({ deviceId });
                // Unlinked immediately — useDevices' live snapshot drops this
                // card. The Wi-Fi clear lands on the device's next check-in
                // (within 5 min) if it's online.
                setMsg('Button unlinked.');
              } catch (e) {
                setMsg(e instanceof Error ? e.message : 'Failed to unlink button.');
                setBusy(false);
              }
            }}
          >
            Unlink button
          </Button>
        </div>
        {msg && <div className="rounded-md bg-paper-alt px-3 py-2 text-sm text-ink-soft">{msg}</div>}
      </CardBody>
      )}
    </Card>
  );
}
